// src/pages/About.jsx
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

import aboutHeroImg from '../assets/images/about-hero.jpg';
import ctaImg from '../assets/images/cta-about.jpg';

const SUPPORT_CARDS = [
  { title: 'Operational continuity', desc: 'We support application operations monitoring and ongoing system support where uninterrupted service is required.' },
  { title: 'Staffing and delivery stability', desc: 'Our workforce model reduces disruption caused by hiring gaps, turnover or short-term staffing constraints.' },
  { title: 'Modernization alongside operations', desc: 'We assist organizations managing legacy upgrades and modernization while continuing day-to-day operations.' },
  { title: 'Compliance and documentation', desc: 'Services are delivered with defined documentation standards, reporting and traceability aligned to organizational requirements.' },
];

const APPROACH_CARDS = [
  { title: 'Discovery', desc: 'Work is delivered through clearly defined scopes, processes and escalation paths.', icon: 'discovery' },
  { title: 'Architecture', desc: 'All deployed professionals remain accountable through internal oversight and performance monitoring.', icon: 'architecture' },
  { title: 'Delivery', desc: 'Training and workforce development are aligned with real operational roles rather than short-term staffing needs.', icon: 'delivery' },
  { title: 'Optimization', desc: 'Documentation, reporting and traceability are treated as core delivery elements.', icon: 'optimization' },
];

function CardIcon() {
  return (
    <div style={{
      background: 'var(--primary-100)',
      border: '2px solid var(--primary-200)',
      borderRadius: '9999px',
      padding: '16px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#1840b5" strokeWidth="1.5">
        <path d="M16 4L4 10v12l12 6 12-6V10L16 4z"/>
        <path d="M4 10l12 6 12-6"/>
        <path d="M16 16v12"/>
      </svg>
    </div>
  );
}

function ApproachIconWrapper({ icon }) {
  const iconMap = {
    discovery: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1840b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
    ),
    architecture: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1840b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="4" rx="1"/><rect x="2" y="13" width="20" height="4" rx="1"/>
        <line x1="6" y1="9" x2="6" y2="13"/><line x1="18" y1="9" x2="18" y2="13"/>
      </svg>
    ),
    delivery: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1840b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    optimization: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1840b5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
  };
  return (
    <div style={{
      background: 'var(--primary-100)',
      border: '1.5px solid var(--primary-200)',
      borderRadius: '9999px',
      width: '36px', height: '36px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, marginBottom: '12px',
    }}>
      {iconMap[icon]}
    </div>
  );
}

export default function About() {
  return (
    <>
      <SEO
        title="About Us"
        description="DistrictSourcingTech delivers structured workforce and managed IT services to support continuity, compliance and predictable delivery. Learn about our purpose, scope and delivery approach."
        url="https://districtsourcingtech.com/about"
        keywords="about District Sourcing Tech, IT services company, workforce management, government IT, managed services"
      />

      <Navbar variant="light" />

      {/* ===== HERO ===== */}
      <section className="section" style={{ paddingBottom: '0', paddingTop: '128px' }}>
        <div style={{ maxWidth: '1376px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '64px', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', position: 'relative' }}>
            <p className="body-lg" style={{ color: 'var(--primary-500)', marginBottom: '16px' }}>About us</p>
            <h1 className="title-1" style={{ marginBottom: '16px' }}>Who we are</h1>
            <p className="body-lg" style={{ color: 'var(--token-desc)', maxWidth: '469px', margin: '0 auto' }}>
              DistrictSourcingTech delivers structured workforce and managed IT services to support continuity, compliance, and predictable delivery.
            </p>
          </div>
          <Reveal style={{ width: '100%', aspectRatio: '1376/738', borderRadius: '8px', overflow: 'hidden' }}>
            <img src={aboutHeroImg} alt="District Sourcing Tech team" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="eager" />
          </Reveal>
        </div>
      </section>

      {/* ===== PURPOSE ===== */}
      <section className="section">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <div className="about-purpose-layout">
            <Reveal style={{ flex: 1 }}>
              <h2 className="title-2">Our purpose and scope</h2>
            </Reveal>
            <Reveal delay={150} style={{ flex: 1 }}>
              <p className="body-lg" style={{ color: 'var(--token-desc)' }}>
                DistrictSourcingTech is built to support organizations that operate complex technology environments and require stability, clear accountability and long-term service continuity. Our focus is on maintaining and modernizing systems without introducing unnecessary delivery or staffing risk.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== WHERE WE ADD SUPPORT ===== */}
      <section className="section section--alt">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="title-2" style={{ maxWidth: '768px', margin: '0 auto 16px' }}>Where we add support</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)', maxWidth: '704px', margin: '0 auto' }}>
              Challenges related to continuity, staffing constraints, system modernization and compliance are addressed through structured delivery and workforce support.
            </p>
          </Reveal>

          <div className="grid-2-col">
            {SUPPORT_CARDS.map((card, i) => (
              <Reveal key={card.title} delay={i * 100} className="support-card">
                <CardIcon />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 className="title-5">{card.title}</h3>
                  <p className="body-lg" style={{ color: 'var(--token-desc)' }}>{card.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMMITMENT BANNER ===== */}
      <Reveal as="section" style={{ background: 'var(--primary-500)', padding: '112px 32px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: '1376px', width: '100%', textAlign: 'center' }}>
          <h2 className="title-1" style={{ color: '#fff', marginBottom: '16px' }}>Our commitment</h2>
          <p className="body-lg" style={{ color: 'var(--shades-200)', maxWidth: '616px', margin: '0 auto' }}>
            DistrictSourcingTech is committed to continuity, accountability and compliance across all workforce services, managed IT engagements and training programs.
          </p>
        </div>
      </Reveal>

      {/* ===== DELIVERY APPROACH ===== */}
      <section className="section">
        <div style={{ maxWidth: '1376px', margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="title-1" style={{ maxWidth: '768px', margin: '0 auto 16px' }}>Delivery approach</h2>
            <p className="body-lg" style={{ color: 'var(--token-desc)', maxWidth: '612px', margin: '0 auto' }}>
              Our delivery approach prioritizes structure, documentation and accountability over informal or experimental execution.
            </p>
          </Reveal>

          <div className="grid-2-col">
            {APPROACH_CARDS.map((card, i) => (
              <Reveal
                key={card.title}
                delay={i * 100}
                style={{ display: 'flex', flexDirection: 'column', padding: '24px', background: 'var(--shades-200)', borderRadius: 'var(--radius-lg)' }}
              >
                <ApproachIconWrapper icon={card.icon} />
                <h3 className="title-6" style={{ marginBottom: '8px' }}>{card.title}</h3>
                <p className="body-sm" style={{ color: 'var(--token-desc)' }}>{card.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <Reveal as="div" className="cta-section">
        <div className="cta-inner">
          <div className="cta-content">
            <div>
              <h2 className="title-2" style={{ color: '#fff', marginBottom: '8px' }}>Ready to get started?</h2>
              <p className="body-lg" style={{ color: 'var(--shades-200)', maxWidth: '432px' }}>
                Reach out to discuss workforce support, managed IT services or training needs.
              </p>
            </div>
            <Link to="/contact" className="btn btn-light">Contact us</Link>
          </div>
          <div className="cta-image">
            <img src={ctaImg} alt="Get started" loading="lazy" />
          </div>
        </div>
      </Reveal>

      <Footer />
    </>
  );
}
